import { supabase } from '../supabaseClient';

// Serviço para gerenciar dados meteorológicos com cache no Supabase
export class DadosMeteorologicosService {
  static requisicoesNASAEmAndamento = new Map();
  static requisicoesClimatologiaEmAndamento = new Map();
  static nasaCooldownUntil = 0;
  static NASA_COOLDOWN_MS = 5 * 60 * 1000;
  static CLIMATOLOGIA_CACHE_MS = 180 * 24 * 60 * 60 * 1000;
  static CLIMATOLOGIA_INICIO = 1991;
  static CLIMATOLOGIA_FIM = 2020;

  static chaveCacheClimatologia(coordenadaX, coordenadaY) {
    const longitude = this.normalizarCoordenada(coordenadaX);
    const latitude = this.normalizarCoordenada(coordenadaY);
    return `climorax_climatologia_v2_${this.CLIMATOLOGIA_INICIO}_${this.CLIMATOLOGIA_FIM}_${latitude.toFixed(2)}_${longitude.toFixed(2)}`;
  }

  static lerCacheClimatologia(coordenadaX, coordenadaY) {
    if (typeof localStorage === 'undefined') return null;

    try {
      const registro = JSON.parse(localStorage.getItem(
        this.chaveCacheClimatologia(coordenadaX, coordenadaY)
      ));
      if (!registro?.parametros || !registro?.timestamp) return null;
      return registro;
    } catch (error) {
      console.warn('⚠️ Cache local de climatologia inválido; uma nova consulta será realizada.', error);
      return null;
    }
  }

  static salvarCacheClimatologia(coordenadaX, coordenadaY, parametros) {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(
        this.chaveCacheClimatologia(coordenadaX, coordenadaY),
        JSON.stringify({ parametros, timestamp: Date.now() })
      );
    } catch (error) {
      console.warn('⚠️ Não foi possível salvar a climatologia no cache local.', error);
    }
  }

  // Uma única climatologia atende Info e Clima do Talhão. A resposta muda pouco,
  // portanto pode ser reutilizada por seis meses e também como fallback offline/429.
  static async buscarClimatologia(coordenadaX, coordenadaY) {
    const longitude = this.normalizarCoordenada(coordenadaX);
    const latitude = this.normalizarCoordenada(coordenadaY);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      throw new Error('Coordenadas inválidas para consultar o histórico climático.');
    }

    const chave = `${latitude.toFixed(2)}:${longitude.toFixed(2)}`;
    const cache = this.lerCacheClimatologia(longitude, latitude);
    const cacheAtual = cache && (Date.now() - cache.timestamp) < this.CLIMATOLOGIA_CACHE_MS;
    if (cacheAtual) return cache.parametros;

    if (this.requisicoesClimatologiaEmAndamento.has(chave)) {
      return this.requisicoesClimatologiaEmAndamento.get(chave);
    }

    const requisicao = (async () => {
      try {
        const parametros = await this.buscarClimatologiaViaProxy(longitude, latitude);
        if (!parametros || Object.keys(parametros).length === 0) {
          throw new Error('A NASA POWER respondeu sem dados de climatologia para este local.');
        }

        this.salvarCacheClimatologia(longitude, latitude, parametros);
        return parametros;
      } catch (error) {
        if (cache?.parametros) {
          console.warn('⚠️ NASA POWER indisponível; usando climatologia preservada no cache.', error);
          return cache.parametros;
        }
        throw error;
      }
    })();

    this.requisicoesClimatologiaEmAndamento.set(chave, requisicao);
    try {
      return await requisicao;
    } finally {
      this.requisicoesClimatologiaEmAndamento.delete(chave);
    }
  }

  static async buscarClimatologiaViaProxy(coordenadaX, coordenadaY) {
    if (Date.now() < this.nasaCooldownUntil) {
      throw new Error('A NASA POWER está em pausa temporária após atingir o limite de consultas. Tente novamente em alguns minutos.');
    }

    const { data, error } = await supabase.functions.invoke('nasa-power', {
      body: {
        tipo: 'climatologia',
        longitude: this.normalizarCoordenada(coordenadaX),
        latitude: this.normalizarCoordenada(coordenadaY),
        inicio: this.CLIMATOLOGIA_INICIO,
        fim: this.CLIMATOLOGIA_FIM,
      },
    });

    if (error) {
      const status = Number(error?.context?.status || error?.status);
      if (status === 429) {
        this.nasaCooldownUntil = Date.now() + this.NASA_COOLDOWN_MS;
        throw new Error('A NASA POWER atingiu o limite temporário de consultas. Aguarde alguns minutos e tente novamente.');
      }

      // Compatibilidade com a função publicada antes do suporte ao tipo
      // "climatologia". A rota NASA POWER é pública e permite CORS; o retorno
      // continua protegido pelo cache local e pelo bloqueio de chamadas iguais.
      console.warn('⚠️ Proxy de climatologia indisponível; tentando a fonte pública da NASA POWER.', error);
      return this.buscarClimatologiaDireta(coordenadaX, coordenadaY);
    }
    if (!data?.ok) {
      if (data?.status === 429) {
        const retryAfter = Number(data.retryAfter);
        const cooldownMs = Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : this.NASA_COOLDOWN_MS;
        this.nasaCooldownUntil = Date.now() + cooldownMs;
        throw new Error('A NASA POWER atingiu o limite temporário de consultas. Aguarde alguns minutos e tente novamente.');
      }

      console.warn('⚠️ Proxy não entregou a climatologia; tentando a fonte pública da NASA POWER.', data?.error);
      return this.buscarClimatologiaDireta(coordenadaX, coordenadaY);
    }

    return data.data?.properties?.parameter || null;
  }

  static async buscarClimatologiaDireta(coordenadaX, coordenadaY) {
    const longitude = this.normalizarCoordenada(coordenadaX);
    const latitude = this.normalizarCoordenada(coordenadaY);
    const params = new URLSearchParams({
      parameters: 'T2M,T2M_MAX,T2M_MIN,PRECTOTCORR,RH2M',
      community: 'AG',
      longitude: String(longitude),
      latitude: String(latitude),
      start: String(this.CLIMATOLOGIA_INICIO),
      end: String(this.CLIMATOLOGIA_FIM),
      format: 'JSON',
    });

    const response = await this.fetchWithRetries(
      `https://power.larc.nasa.gov/api/temporal/climatology/point?${params}`,
      { headers: { Accept: 'application/json' } },
      2,
      1500
    );

    if (!response?.ok) {
      throw new Error(`A NASA POWER respondeu com status ${response?.status || 'indisponível'} ao consultar o histórico climático.`);
    }

    const data = await response.json();
    const parametros = data?.properties?.parameter;
    if (!parametros || Object.keys(parametros).length === 0) {
      throw new Error('A NASA POWER respondeu sem dados de climatologia para este local.');
    }

    return parametros;
  }
  
  // Buscar dados meteorológicos (primeiro do cache, depois da API se necessário)
  static async obterDadosMeteorologicos(talhaoId, coordenadaX, coordenadaY, dataInicio, dataFim) {
    try {
      const inicioNormalizado = String(dataInicio || '').slice(0, 10);
      const fimNormalizado = String(dataFim || '').slice(0, 10);
      const dataPattern = /^\d{4}-\d{2}-\d{2}$/;
      if (!dataPattern.test(inicioNormalizado) || !dataPattern.test(fimNormalizado)) {
        throw new Error('Período meteorológico inválido. Informe datas no formato correto.');
      }
      if (inicioNormalizado > fimNormalizado) {
        console.warn('⚠️ Período meteorológico sem observações: a data inicial é posterior à data final.', {
          dataInicio: inicioNormalizado,
          dataFim: fimNormalizado,
        });
        return [];
      }

      dataInicio = inicioNormalizado;
      dataFim = fimNormalizado;
      console.log('🔍 Buscando dados meteorológicos...', { talhaoId, coordenadaX, coordenadaY, dataInicio, dataFim });
      
      // 1. Verificar dados existentes no Supabase
      const dadosExistentes = await this.buscarDadosCache(talhaoId, dataInicio, dataFim);
      console.log('💾 Dados encontrados no cache:', dadosExistentes.length);

      // 2. Reaproveitar observações do mesmo ponto já buscadas para outros talhões acessíveis
      const periodosSemCacheLocal = this.identificarPeriodosFaltantes(dadosExistentes, dataInicio, dataFim);
      let dadosCompartilhados = [];
      if (periodosSemCacheLocal.length > 0) {
        dadosCompartilhados = await this.buscarDadosCacheLocalizacao(
          coordenadaX,
          coordenadaY,
          dataInicio,
          dataFim,
          talhaoId
        );

        if (dadosCompartilhados.length > 0) {
          try {
            await this.salvarDadosCache(talhaoId, coordenadaX, coordenadaY, dadosCompartilhados);
          } catch (cacheError) {
            console.warn('⚠️ Não foi possível vincular o cache compartilhado ao talhão.', cacheError);
          }
        }
      }
      
      // 3. Identificar somente os períodos que nenhum cache possui
      const dadosEmCache = [...dadosExistentes, ...dadosCompartilhados];
      const periodosFaltantes = this.identificarPeriodosFaltantes(dadosEmCache, dataInicio, dataFim);
      console.log('🔄 Períodos faltantes:', periodosFaltantes);
      
      // 4. Buscar dados faltantes na API NASA POWER
      let novosDados = [];
      if (periodosFaltantes.length > 0) {
        try {
          novosDados = await this.buscarDadosNASAPOWER(coordenadaX, coordenadaY, periodosFaltantes);

          // 5. Salvar novos dados no cache
          if (novosDados.length > 0) {
            await this.salvarDadosCache(talhaoId, coordenadaX, coordenadaY, novosDados);
          }
        } catch (nasaError) {
          if (dadosEmCache.length === 0) throw nasaError;
          console.warn('⚠️ NASA POWER indisponível; mantendo os dados já existentes no cache.', nasaError);
        }
      }
      
      // 6. Combinar dados existentes + compartilhados + novos
      const todosOsDados = [...dadosEmCache, ...novosDados].sort((a, b) =>
        new Date(a.data) - new Date(b.data)
      );

      const dadosUnicos = [...new Map(
        todosOsDados.map(registro => [String(registro.data).slice(0, 10), registro])
      ).values()];

      if (dadosUnicos.length === 0) {
        throw new Error('A NASA POWER não disponibilizou dados válidos para este período e localização. Tente novamente mais tarde ou selecione um período anterior.');
      }
      
      console.log('✅ Total de dados retornados:', dadosUnicos.length);
      return dadosUnicos;
      
    } catch (error) {
      console.error('❌ Erro ao obter dados meteorológicos:', error);
      throw error;
    }
  }
  
  // Buscar dados no cache do Supabase
  static async buscarDadosCache(talhaoId, dataInicio, dataFim) {
    console.log('🔍 Buscando dados no cache do Supabase:', {
      talhaoId,
      dataInicio,
      dataFim
    });
    
    const { data, error } = await supabase
      .from('dados_meteorologicos')
      .select('*')
      .eq('talhao_id', talhaoId)
      .gte('data', dataInicio)
      .lte('data', dataFim)
      .order('data', { ascending: true });
    
    if (error) {
      console.error('❌ Erro ao buscar dados do cache:', {
        error,
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      return [];
    }
    
    console.log('📊 Dados encontrados no cache:', {
      quantidade: data?.length || 0,
      exemplos: data?.slice(0, 2)
    });
    
    return data || [];
  }

  static normalizarCoordenada(coordenada) {
    return Number(Number(coordenada).toFixed(6));
  }

  // Reutiliza somente dados do mesmo ponto exato; a RLS limita a consulta aos talhões acessíveis.
  static async buscarDadosCacheLocalizacao(coordenadaX, coordenadaY, dataInicio, dataFim, talhaoId) {
    const longitude = this.normalizarCoordenada(coordenadaX);
    const latitude = this.normalizarCoordenada(coordenadaY);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return [];

    const { data, error } = await supabase
      .from('dados_meteorologicos')
      .select('data, precipitacao, temp_max, temp_min, umidade, vento, radiacao, et0')
      .eq('coordenada_x', longitude)
      .eq('coordenada_y', latitude)
      .neq('talhao_id', talhaoId)
      .gte('data', dataInicio)
      .lte('data', dataFim)
      .order('data', { ascending: true });

    if (error) {
      console.warn('⚠️ Não foi possível consultar o cache meteorológico compartilhado:', error.message || error);
      return [];
    }

    return [...new Map(
      (data || []).map(registro => [String(registro.data).slice(0, 10), registro])
    ).values()];
  }
  
  // Identificar quais períodos não estão no cache
  static identificarPeriodosFaltantes(dadosExistentes, dataInicio, dataFim) {
    const periodosFaltantes = [];
    const datasExistentes = new Set(dadosExistentes.map(d => d.data));
    
    // Gerar todas as datas do período
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    const dataAtual = new Date(inicio);
    
    while (dataAtual <= fim) {
      const dataStr = dataAtual.toISOString().split('T')[0];
      if (!datasExistentes.has(dataStr)) {
        periodosFaltantes.push(dataStr);
      }
      dataAtual.setDate(dataAtual.getDate() + 1);
    }
    
    // Agrupar datas consecutivas em períodos
    return this.agruparPeriodosConsecutivos(periodosFaltantes);
  }
  
  // Agrupar datas consecutivas em períodos para otimizar chamadas à API
  static agruparPeriodosConsecutivos(datas) {
    if (datas.length === 0) return [];
    
    const periodos = [];
    let inicio = datas[0];
    let fim = datas[0];
    
    for (let i = 1; i < datas.length; i++) {
      const dataAnterior = new Date(fim);
      const dataAtual = new Date(datas[i]);
      const diferenca = (dataAtual - dataAnterior) / (1000 * 60 * 60 * 24);
      
      if (diferenca === 1) {
        // Data consecutiva
        fim = datas[i];
      } else {
        // Quebra na sequência
        periodos.push({ inicio, fim });
        inicio = datas[i];
        fim = datas[i];
      }
    }
    
    periodos.push({ inicio, fim });
    return periodos;
  }

  // Helper: fetch com retries e logs melhores para falhas de rede/DNS
  static async fetchWithRetries(url, options = {}, retries = 3, delayMs = 1500) {
    if (Date.now() < this.nasaCooldownUntil) {
      throw new Error('A NASA POWER está em pausa temporária após atingir o limite de consultas. Tente novamente em alguns minutos.');
    }

    for (let attempt = 1; attempt <= retries; attempt++) {
      let response;

      try {
        response = await fetch(url, options);
      } catch (err) {
        console.warn(`⚠️ fetch falhou (tentativa ${attempt}/${retries}):`, err.message || err);

        if (attempt === retries) {
          throw new Error('Não foi possível conectar à NASA POWER. Verifique sua conexão e tente novamente.');
        }

        await new Promise(res => setTimeout(res, delayMs * attempt));
        continue;
      }

      if (response.status !== 429) {
        return response;
      }

      console.warn(`⚠️ NASA POWER limitou as requisições (tentativa ${attempt}/${retries}).`);
      const retryAfter = Number(response.headers?.get?.('Retry-After'));
      const cooldownMs = Number.isFinite(retryAfter) && retryAfter > 0
        ? retryAfter * 1000
        : this.NASA_COOLDOWN_MS;
      this.nasaCooldownUntil = Date.now() + cooldownMs;
      throw new Error('A NASA POWER atingiu o limite temporário de consultas. Aguarde alguns minutos e tente novamente.');
    }
  }
  
  // Buscar dados na API NASA POWER
  static async buscarDadosNASAPOWER(coordenadaX, coordenadaY, periodos) {
    const chave = JSON.stringify([
      this.normalizarCoordenada(coordenadaX),
      this.normalizarCoordenada(coordenadaY),
      periodos
    ]);

    if (this.requisicoesNASAEmAndamento.has(chave)) {
      return this.requisicoesNASAEmAndamento.get(chave);
    }

    const requisicao = this.executarBuscaNASAPOWER(coordenadaX, coordenadaY, periodos);
    this.requisicoesNASAEmAndamento.set(chave, requisicao);

    try {
      return await requisicao;
    } finally {
      this.requisicoesNASAEmAndamento.delete(chave);
    }
  }

  static async executarBuscaNASAPOWER(coordenadaX, coordenadaY, periodos) {
    const todosDados = [];
    let ultimaFalha = null;
    
    for (const periodo of periodos) {
      try {
        console.log(`🌍 Buscando dados NASA POWER: ${periodo.inicio} a ${periodo.fim}`);

        const dados = await this.buscarPeriodoViaProxy(
          coordenadaX,
          coordenadaY,
          periodo.inicio,
          periodo.fim
        );
        const parametros = dados.properties?.parameter;
        if (!parametros || Object.keys(parametros).length === 0) {
          throw new Error('A NASA POWER respondeu sem parâmetros meteorológicos.');
        }
        console.log('📡 Dados recebidos da NASA POWER:', Object.keys(parametros));
        
        // Processar dados recebidos
        const dadosProcessados = this.processarDadosNASAPOWER(dados, periodo.inicio, periodo.fim);
        todosDados.push(...dadosProcessados);
        
        // Aguardar um pouco entre chamadas para não sobrecarregar a API
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (error) {
        ultimaFalha = error;
        console.error(`❌ Erro ao buscar dados NASA POWER para período ${periodo.inicio}-${periodo.fim}:`, error);
      }
    }

    if (todosDados.length === 0 && periodos.length > 0) {
      throw ultimaFalha || new Error('A NASA POWER ainda não disponibilizou dados válidos para o período solicitado.');
    }
    
    return todosDados;
  }

  static async buscarPeriodoViaProxy(coordenadaX, coordenadaY, inicio, fim) {
    if (Date.now() < this.nasaCooldownUntil) {
      throw new Error('A NASA POWER está em pausa temporária após atingir o limite de consultas. Tente novamente em alguns minutos.');
    }

    const { data, error } = await supabase.functions.invoke('nasa-power', {
      body: {
        longitude: this.normalizarCoordenada(coordenadaX),
        latitude: this.normalizarCoordenada(coordenadaY),
        inicio,
        fim,
      },
    });

    if (error) {
      const status = Number(error?.context?.status || error?.status);
      if (status === 429) {
        this.nasaCooldownUntil = Date.now() + this.NASA_COOLDOWN_MS;
        throw new Error('A NASA POWER atingiu o limite temporário de consultas. Aguarde alguns minutos e tente novamente.');
      }
      console.warn('⚠️ Proxy diário indisponível; tentando a fonte pública da NASA POWER.', error);
      return this.buscarPeriodoDireto(coordenadaX, coordenadaY, inicio, fim);
    }
    if (!data?.ok) {
      if (data?.status === 429) {
        const retryAfter = Number(data.retryAfter);
        const cooldownMs = Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : this.NASA_COOLDOWN_MS;
        this.nasaCooldownUntil = Date.now() + cooldownMs;
        throw new Error('A NASA POWER atingiu o limite temporário de consultas. Aguarde alguns minutos e tente novamente.');
      }
      console.warn('⚠️ Proxy não entregou o período diário; tentando a fonte pública da NASA POWER.', data?.error);
      return this.buscarPeriodoDireto(coordenadaX, coordenadaY, inicio, fim);
    }

    return data.data;
  }

  static async buscarPeriodoDireto(coordenadaX, coordenadaY, inicio, fim) {
    const params = new URLSearchParams({
      parameters: 'PRECTOTCORR,T2M_MAX,T2M_MIN,RH2M,WS2M,ALLSKY_SFC_SW_DWN',
      community: 'AG',
      longitude: String(this.normalizarCoordenada(coordenadaX)),
      latitude: String(this.normalizarCoordenada(coordenadaY)),
      start: String(inicio).replace(/-/g, ''),
      end: String(fim).replace(/-/g, ''),
      format: 'JSON',
    });
    const response = await this.fetchWithRetries(
      `https://power.larc.nasa.gov/api/temporal/daily/point?${params}`,
      { headers: { Accept: 'application/json' } },
      2,
      1500
    );
    if (!response?.ok) {
      throw new Error(`A NASA POWER respondeu com status ${response?.status || 'indisponível'} ao consultar o período.`);
    }
    const result = await response.json();
    if (!result?.properties?.parameter) {
      throw new Error('A NASA POWER respondeu sem dados meteorológicos para o período.');
    }
    return result;
  }
  
  // Processar e calcular ET0 para os dados da NASA POWER
  static processarDadosNASAPOWER(dados, dataInicio, dataFim) {
    const parameter = dados.properties?.parameter;
    if (!parameter) return [];
    
    const dadosProcessados = [];
    const inicioCompacto = String(dataInicio).replace(/-/g, '');
    const fimCompacto = String(dataFim).replace(/-/g, '');
    const datasRecebidas = Object.keys(parameter.PRECTOTCORR || {})
      .filter(data => data >= inicioCompacto && data <= fimCompacto)
      .sort();

    datasRecebidas.forEach(dataStr => {
      const dataIso = `${dataStr.slice(0, 4)}-${dataStr.slice(4, 6)}-${dataStr.slice(6, 8)}`;
      const dataAtual = new Date(`${dataIso}T12:00:00`);
      
      // Extrair dados do dia
      const precipitacao = parameter.PRECTOTCORR?.[dataStr];
      const tempMax = parameter.T2M_MAX?.[dataStr];
      const tempMin = parameter.T2M_MIN?.[dataStr];
      const umidade = parameter.RH2M?.[dataStr];
      const vento = parameter.WS2M?.[dataStr];
      const radiacao = parameter.ALLSKY_SFC_SW_DWN?.[dataStr];
      
      // Validar dados (NASA POWER retorna -999 ou -99 para dados inválidos)
      if (this.validarDadosMeteorologicos(precipitacao, tempMax, tempMin, umidade, vento, radiacao)) {
        // Calcular ET0 usando Penman-Monteith
        const et0 = this.calcularPenmanMonteithET0(tempMax, tempMin, umidade, vento, radiacao, dataAtual);
        
        dadosProcessados.push({
          data: dataIso,
          precipitacao: precipitacao,
          temp_max: tempMax,
          temp_min: tempMin,
          umidade: this.isValidNasaValue(umidade) ? umidade : null,
          vento: this.isValidNasaValue(vento) ? vento : null,
          radiacao: this.isValidNasaValue(radiacao) ? radiacao : null,
          et0: et0
        });
      }
    });
    
    return dadosProcessados;
  }

  static isValidNasaValue(valor) {
    return valor !== null
      && valor !== undefined
      && Number(valor) !== -999
      && Number(valor) !== -99
      && Number.isFinite(Number(valor));
  }
  
  // Validar se os dados meteorológicos são válidos
  static validarDadosMeteorologicos(precipitacao, tempMax, tempMin) {
    // Chuva e temperaturas são a base mínima do balanço. Umidade, vento e
    // radiação podem chegar depois no produto near-real-time da NASA e possuem
    // substituições agronômicas conservadoras no cálculo da ETo.
    return [precipitacao, tempMax, tempMin].every(valor => this.isValidNasaValue(valor));
  }
  
  // Calcular ET0 usando Penman-Monteith FAO-56
  static calcularPenmanMonteithET0(tempMax, tempMin, umidade, vento, radiacao, data) {
    try {
      const maxima = Number(tempMax);
      const minima = Number(tempMin);
      const tempMedia = (maxima + minima) / 2;
      const umidadeValida = this.isValidNasaValue(umidade) && Number(umidade) >= 0 && Number(umidade) <= 100
        ? Number(umidade)
        : 50;
      const ventoValido = this.isValidNasaValue(vento) && Number(vento) >= 0 ? Number(vento) : 2;
      const amplitudeTermica = Math.max(0, maxima - minima);
      const radiacaoValida = this.isValidNasaValue(radiacao) && Number(radiacao) >= 0
        ? Number(radiacao)
        : 15 * 0.16 * Math.sqrt(amplitudeTermica);
      
      // Constantes
      const gamma = 0.665; // Constante psicrométrica (kPa/°C)
      const albedo = 0.23; // Albedo da grama
      
      // Pressão de vapor de saturação
      const es_tmax = 0.6108 * Math.exp((17.27 * maxima) / (maxima + 237.3));
      const es_tmin = 0.6108 * Math.exp((17.27 * minima) / (minima + 237.3));
      const es = (es_tmax + es_tmin) / 2;
      
      // Pressão de vapor atual
      const ea = (umidadeValida / 100) * es;
      
      // Déficit de pressão de vapor
      const vpd = es - ea;
      
      // Curva de pressão de vapor
      const delta = (4098 * es) / Math.pow(tempMedia + 237.3, 2);
      
      // Radiação líquida
      const rn = radiacaoValida * (1 - albedo); // Simplificado
      
      // Velocidade do vento a 2m
      const u2 = ventoValido;
      
      // Penman-Monteith
      const numerador = 0.408 * delta * rn + gamma * (900 / (tempMedia + 273)) * u2 * vpd;
      const denominador = delta + gamma * (1 + 0.34 * u2);
      
      const et0 = numerador / denominador;
      
      return Math.max(0, Math.round(et0 * 100) / 100); // Arredondar para 2 casas decimais
      
    } catch (error) {
      console.error('❌ Erro no cálculo Penman-Monteith:', error);
      return 0;
    }
  }
  
  // Salvar dados no cache do Supabase
  static async salvarDadosCache(talhaoId, coordenadaX, coordenadaY, dados) {
    if (dados.length === 0) {
      console.log('⚠️ Nenhum dado para salvar no cache');
      return;
    }
    
    console.log('💾 Preparando para salvar no cache:', {
      talhaoId,
      coordenadaX,
      coordenadaY,
      quantidadeDados: dados.length,
      exemplosDados: dados.slice(0, 2)
    });
    
    const dadosParaSalvar = dados.map(dado => ({
      ...dado,
      talhao_id: talhaoId,
      coordenada_x: coordenadaX,
      coordenada_y: coordenadaY
    }));
    // Garantir que o registro contenha o user_id exigido pela RLS
    let userId = null;
    try {
      const { data: userData } = await supabase.auth.getUser();
      userId = userData?.user?.id || null;
    } catch (err) {
      // fallback para compatibilidade com versões antigas do supabase-js
      try {
        // @ts-ignore
        userId = supabase.auth.user?.()?.id || null;
      } catch (e) {
        userId = null;
      }
    }

    const dadosParaSalvarComUser = dadosParaSalvar.map(item => ({
      ...item,
      user_id: userId
    }));

    console.log('🔄 Executando upsert no Supabase...', dadosParaSalvarComUser.slice(0, 1));
    
    const { data, error } = await supabase
      .from('dados_meteorologicos')
      .upsert(dadosParaSalvarComUser, { 
        onConflict: 'talhao_id,data',
        ignoreDuplicates: false 
      })
      .select();
    
    if (error) {
      console.error('❌ Erro ao salvar dados no cache:', {
        error,
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }
    
    console.log('✅ Dados salvos no cache com sucesso:', {
      dadosSalvos: data?.length || dadosParaSalvar.length,
      exemploRetorno: data?.[0]
    });
  }
  
  // Salvar dados do balanço hídrico na nova tabela balanco_hidrico
  static async salvarBalancoHidrico(talhaoId, coordenadaX, coordenadaY, dadosBalanco) {
    try {
      console.log('💧 Salvando dados do balanço hídrico na tabela balanco_hidrico...', {
        talhaoId,
        coordenadaX,
        coordenadaY,
        dadosBalanco
      });

      // Preparar dados para salvar na nova tabela
      const dataAtual = new Date().toISOString().split('T')[0];
      
      // Calcular data_inicio e data_fim baseado nos dados do balanço
      const hoje = new Date();
      let dataInicio, dataFim;
      
      if (dadosBalanco.dataPlantio) {
        dataInicio = new Date(dadosBalanco.dataPlantio).toISOString().split('T')[0];
        dataFim = dataAtual;
      } else {
        // Se não tem data de plantio, usar os últimos diasAnalisados
        const diasAnalisados = dadosBalanco.diasAnalisados || 30;
        dataInicio = new Date(hoje);
        dataInicio.setDate(dataInicio.getDate() - diasAnalisados);
        dataInicio = dataInicio.toISOString().split('T')[0];
        dataFim = dataAtual;
      }
      
      const dadosBalancoParaSalvar = {
        talhao_id: parseInt(talhaoId),
        data_calculo: dataAtual,
        data_inicio: dataInicio,
        data_fim: dataFim,
        
        // Dados agregados do período
        total_precipitacao: parseFloat(dadosBalanco.totalPrecipitacao) || 0,
        total_etp: parseFloat(dadosBalanco.totalETP) || 0,
        balanco_final: parseFloat(dadosBalanco.balanco) || 0,
        
        // Médias do período
        temperatura_media: parseFloat(dadosBalanco.mediaTemp) || 0,
        umidade_media: parseFloat(dadosBalanco.mediaUmidade) || 0,
        
        // Metadados
        dias_analisados: parseInt(dadosBalanco.diasAnalisados) || 0,
        dias_desde_plantio: dadosBalanco.diasDesdeePlantio || null,
        data_plantio: dadosBalanco.dataPlantio || null,
        
        // Qualidade dos dados
        dias_precipitacao_valida: dadosBalanco.dadosValidos?.precipitacao || 0,
        dias_temperatura_valida: dadosBalanco.dadosValidos?.temperatura || 0,
        dias_umidade_valida: dadosBalanco.dadosValidos?.umidade || 0,
        
        // Coordenadas (para referência)
        coordenada_x: parseFloat(coordenadaX),
        coordenada_y: parseFloat(coordenadaY)
      };

      console.log('📊 Dados preparados para salvar na tabela balanco_hidrico:', dadosBalancoParaSalvar);

      // Salvar na nova tabela balanco_hidrico
      const { data: insertData, error: insertError } = await supabase
        .from('balanco_hidrico')
        .insert([dadosBalancoParaSalvar])
        .select();

      if (insertError) {
        console.warn('⚠️ Falha na inserção, tentando upsert:', insertError.message);
        
        // Se falhar, tentar upsert (pode ser um registro duplicado para o mesmo período)
        const { data: upsertData, error: upsertError } = await supabase
          .from('balanco_hidrico')
          .upsert(dadosBalancoParaSalvar, {
            onConflict: ['talhao_id', 'data_inicio', 'data_fim']
          })
          .select();

        if (upsertError) {
          console.error('❌ Erro no upsert:', {
            error: upsertError,
            message: upsertError.message,
            details: upsertError.details,
            hint: upsertError.hint,
            code: upsertError.code
          });
          throw upsertError;
        }

        console.log('✅ Balanço hídrico salvo via upsert:', upsertData);
        return upsertData;
      }

      console.log('✅ Balanço hídrico salvo via insert:', insertData);
      return insertData;

    } catch (error) {
      console.error('❌ Erro geral ao salvar balanço hídrico:', error);
      throw error;
    }
  }

  // Salvar dados meteorológicos diários para gráficos
  static async salvarDadosDiarios(talhaoId, coordenadaX, coordenadaY, dadosClima, userId) {
    try {
      if (!dadosClima || !dadosClima.properties || !dadosClima.properties.parameter) {
        console.warn('⚠️ Dados climáticos inválidos para salvar dados diários');
        return;
      }

      const params = dadosClima.properties.parameter;
      const precipitacao = params.PRECTOTCORR || {};
      const tempMax = params.T2M_MAX || {};
      const tempMin = params.T2M_MIN || {};
      const umidade = params.RH2M || {};
      const vento = params.WS2M || {};
      const radiacao = params.ALLSKY_SFC_SW_DWN || {};

      const datas = Object.keys(precipitacao);
      const dadosDiarios = [];

      console.log('📊 Preparando dados diários para salvar:', datas.length, 'dias');

      datas.forEach(data => {
        const dataFormatada = `${data.substring(0,4)}-${data.substring(4,6)}-${data.substring(6,8)}`;
        
        const prec = precipitacao[data];
        const tMax = tempMax[data];
        const tMin = tempMin[data];
        const um = umidade[data];
        const wind = vento[data];
        const rad = radiacao[data];

        // Validar se tem dados mínimos
        if (this.validarDadosMeteorologicos(prec, tMax, tMin, um, wind, rad)) {
          const tempMedia = (tMax + tMin) / 2;
          const et0 = this.calcularPenmanMonteithET0(tMax, tMin, um, wind, rad, dataFormatada);

          dadosDiarios.push({
            talhao_id: parseInt(talhaoId),
            coordenada_x: parseFloat(coordenadaX),
            coordenada_y: parseFloat(coordenadaY),
            data: dataFormatada,
            precipitacao: parseFloat(prec) || 0,
            temp_max: parseFloat(tMax) || null,
            temp_min: parseFloat(tMin) || null,
            temp_media: parseFloat(tempMedia) || null,
            umidade: parseFloat(um) || null,
            vento: parseFloat(wind) || null,
            radiacao: parseFloat(rad) || null,
            et0: parseFloat(et0) || null,
            user_id: userId
          });
        }
      });

      if (dadosDiarios.length === 0) {
        console.warn('⚠️ Nenhum dado diário válido para salvar');
        return;
      }

      console.log('💾 Salvando', dadosDiarios.length, 'registros diários...');

      // Salvar em lotes para evitar problemas de performance
      const loteSize = 50;
      for (let i = 0; i < dadosDiarios.length; i += loteSize) {
        const lote = dadosDiarios.slice(i, i + loteSize);
        
        const { data, error } = await supabase
          .from('dados_meteorologicos')
          .upsert(lote, {
            onConflict: 'talhao_id,data'
          })
          .select();

        if (error) {
          console.error('❌ Erro ao salvar lote de dados diários:', error);
          throw error;
        }

        console.log(`✅ Lote ${Math.floor(i/loteSize) + 1} salvo:`, data?.length || lote.length, 'registros');
      }

      console.log('🎉 Todos os dados diários salvos com sucesso!');
      return dadosDiarios.length;

    } catch (error) {
      console.error('❌ Erro geral ao salvar dados diários:', error);
      throw error;
    }
  }

  // Buscar dados meteorológicos diários para gráficos
  static async buscarDadosDiarios(talhaoId, dataInicio = null, dataFim = null) {
    try {
      console.log('📊 Buscando dados diários...', { talhaoId, dataInicio, dataFim });

      let query = supabase
        .from('dados_meteorologicos')
        .select('*')
        .eq('talhao_id', parseInt(talhaoId))
        .order('data', { ascending: true });

      if (dataInicio) {
        query = query.gte('data', dataInicio);
      }
      
      if (dataFim) {
        query = query.lte('data', dataFim);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Erro ao buscar dados diários:', error);
        throw error;
      }

      console.log('✅ Dados diários obtidos:', data?.length || 0, 'registros');
      return data || [];

    } catch (error) {
      console.error('❌ Erro geral ao buscar dados diários:', error);
      throw error;
    }
  }

  // Buscar histórico de balanços hídricos na nova tabela
  static async buscarHistoricoBalancoHidrico(talhaoId, limite = 30) {
    try {
      console.log('📊 Buscando histórico de balanço hídrico na tabela balanco_hidrico...', { talhaoId, limite });

      // Buscar diretamente na tabela balanco_hidrico
      const { data, error } = await supabase
        .from('balanco_hidrico')
        .select('*')
        .eq('talhao_id', parseInt(talhaoId))
        .order('data_calculo', { ascending: false })
        .limit(limite);

      if (error) {
        console.error('❌ Erro ao buscar histórico de balanço:', error);
        throw error;
      }

      console.log('✅ Histórico de balanço hídrico obtido:', {
        quantidade: data?.length || 0,
        registros: data
      });
      
      return data || [];

    } catch (error) {
      console.error('❌ Erro geral ao buscar histórico:', error);
      throw error;
    }
  }

  // Buscar dados diários para gráficos baseado no ciclo da cultura
  static async buscarDadosDiariosParaGraficos(talhaoId, dataPlantio = null, dataColheita = null) {
    try {
      console.log('📈 Buscando dados diários para gráficos do ciclo da cultura...', { 
        talhaoId, 
        dataPlantio, 
        dataColheita 
      });

      // Definir período de análise baseado no ciclo da cultura
      let dataInicio, dataFim;
      
      if (dataPlantio) {
        dataInicio = new Date(dataPlantio).toISOString().split('T')[0];
        dataFim = dataColheita ? 
          new Date(dataColheita).toISOString().split('T')[0] : 
          new Date().toISOString().split('T')[0];
      } else {
        // Fallback: últimos 90 dias se não houver data de plantio
        const hoje = new Date();
        dataInicio = new Date(hoje.setDate(hoje.getDate() - 90)).toISOString().split('T')[0];
        dataFim = new Date().toISOString().split('T')[0];
      }

      console.log('📅 Período do gráfico:', {
        dataInicio,
        dataFim,
        origem: dataPlantio ? 'ciclo da cultura' : 'últimos 90 dias'
      });

      // Buscar dados no período definido
      const { data, error } = await supabase
        .from('dados_meteorologicos')
        .select('data, precipitacao, et0, temp_max, temp_min, umidade')
        .eq('talhao_id', parseInt(talhaoId))
        .gte('data', dataInicio)
        .lte('data', dataFim)
        .order('data', { ascending: true });

      if (error) {
        console.error('❌ Erro ao buscar dados diários:', error);
        throw error;
      }

      console.log('📊 Dados encontrados para gráficos:', {
        quantidade: data?.length || 0,
        primeiroRegistro: data?.[0],
        ultimoRegistro: data?.[data.length - 1]
      });

      if (!data || data.length === 0) {
        console.log('⚠️ Nenhum dado encontrado para o período da cultura');
        return [];
      }

      // Processar dados para o gráfico (já em ordem cronológica)
      const dadosProcessados = data?.map(item => ({
        data: item.data,
        dataFormatada: new Date(item.data).toLocaleDateString('pt-BR', { 
          day: '2-digit', 
          month: '2-digit' 
        }),
        precipitacao: parseFloat(item.precipitacao) || 0,
        et0: parseFloat(item.et0) || 0,
        tempMax: parseFloat(item.temp_max) || null,
        tempMin: parseFloat(item.temp_min) || null,
        umidade: parseFloat(item.umidade) || null
      })) || [];

      console.log('✅ Dados diários processados:', {
        quantidade: dadosProcessados.length,
        periodo: dadosProcessados.length > 0 ? {
          inicio: dadosProcessados[0]?.data,
          fim: dadosProcessados[dadosProcessados.length - 1]?.data
        } : null
      });

      return dadosProcessados;

    } catch (error) {
      console.error('❌ Erro geral ao buscar dados diários:', error);
      throw error;
    }
  }

  // Limpar dados antigos do cache (opcional - para manutenção)
  static async limparCacheAntigo(diasParaManter = 365) {
    const dataLimite = new Date();
    dataLimite.setDate(dataLimite.getDate() - diasParaManter);

    const { error } = await supabase
      .from('dados_meteorologicos')
      .delete()
      .lt('data', dataLimite.toISOString().split('T')[0]);

    if (error) {
      console.error('❌ Erro ao limpar cache antigo:', error);
    } else {
      console.log('🧹 Cache antigo limpo com sucesso');
    }
  }

  /**
   * Calcula médias dos últimos `janela` dias disponíveis no cache NASA POWER.
   * Usado para preencher dias de delay onde o NASA POWER ainda não publicou os dados.
   * Precipitação é forçada para 0 (conservador — não assume chuva não confirmada).
   *
   * @param {Array<string|number>} cacheIds  - IDs a tentar (estacao_id e/ou talhao_id)
   * @param {string} dataReferencia          - Data alvo 'YYYY-MM-DD'
   * @param {number} janela                  - Número de dias de referência (padrão 7)
   * @returns {Object|null} Objeto com campos meteorológicos médios, ou null se sem dados
   */
  static async calcularMediaUltimosDias(cacheIds, dataReferencia, janela = 7) {
    try {
      const dataRef = new Date(dataReferencia + 'T12:00:00');
      const dataFim = new Date(dataRef);
      dataFim.setDate(dataFim.getDate() - 1); // até o dia anterior
      const dataInicio = new Date(dataFim);
      dataInicio.setDate(dataInicio.getDate() - (janela * 3)); // janela mais larga para garantir dados

      const fimStr    = dataFim.toISOString().split('T')[0];
      const inicioStr = dataInicio.toISOString().split('T')[0];

      let registros = [];
      for (const cacheId of cacheIds) {
        if (!cacheId) continue;
        const dados = await this.buscarDadosCache(cacheId, inicioStr, fimStr);
        if (dados && dados.length > 0) {
          registros = dados;
          break; // usa o primeiro ID que tiver dados
        }
      }

      if (registros.length === 0) return null;

      // Pegar os últimos `janela` registros válidos
      const ultimos = registros.slice(-janela);
      const n = ultimos.length;
      if (n === 0) return null;

      const soma = (field) => ultimos.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);

      return {
        temperatura_max:  parseFloat((soma('temp_max') / n).toFixed(2)),
        temperatura_min:  parseFloat((soma('temp_min') / n).toFixed(2)),
        umidade_relativa: parseFloat((soma('umidade') / n).toFixed(2)),
        vento_velocidade: parseFloat((soma('vento') / n).toFixed(2)),
        radiacao_solar:   parseFloat((soma('radiacao') / n).toFixed(2)),
        precipitacao_mm:  0,          // conservador: sem chuva assumida nos dias de delay
        fonte_dados:      'media_7d',
        metodo_et0:       `Penman-Monteith (média ${n}d)`,
        _dias_base:       n,
      };
    } catch (error) {
      console.error('❌ Erro ao calcular média dos últimos dias:', error);
      return null;
    }
  }
}

export default DadosMeteorologicosService;
